import { useState, useEffect, useRef, useCallback } from "react";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Bell, Brush, Syringe, CheckCheck, X } from "lucide-react";

function currency(v) {
  return `R$ ${Number(v || 0).toLocaleString("pt-BR", { minimumFractionDigits: 2 })}`;
}

function timeAgo(iso) {
  if (!iso) return "";
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return "agora";
  if (diff < 3600) return `${Math.floor(diff / 60)}min`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
  return `${Math.floor(diff / 86400)}d`;
}

const ROLE_LABELS = {
  admin: "Admin",
  medico: "Médico",
  enfermeira_prescritora: "Enf. Tricologia",
  enfermagem: "Enfermagem",
};

export function TreatmentAlertBell() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [alerts, setAlerts] = useState([]);
  const [unread, setUnread] = useState(0);
  const panelRef = useRef(null);

  const isSecretary = user?.role === "secretaria";

  const fetchUnread = useCallback(async () => {
    if (!isSecretary) return;
    try {
      const { data } = await api.get("/treatment-alerts/unread-count");
      setUnread(data.count || 0);
    } catch (_) {}
  }, [isSecretary]);

  const fetchAlerts = useCallback(async () => {
    if (!isSecretary) return;
    try {
      const { data } = await api.get("/treatment-alerts");
      const unreadOnly = data.filter((a) => !a.read);
      setAlerts(unreadOnly);
      setUnread(unreadOnly.length);
    } catch (_) {}
  }, [isSecretary]);

  // Poll unread count every 30s and on mount
  useEffect(() => {
    fetchUnread();
    const id = setInterval(fetchUnread, 30000);
    return () => clearInterval(id);
  }, [fetchUnread]);

  // Refresh count on live WebSocket alert
  useEffect(() => {
    function handleLive() {
      fetchUnread();
    }
    window.addEventListener("kloss-treatment-alert", handleLive);
    return () => window.removeEventListener("kloss-treatment-alert", handleLive);
  }, [fetchUnread]);

  // Fetch full list when panel opens
  useEffect(() => {
    if (open) fetchAlerts();
  }, [open, fetchAlerts]);

  // Close panel when clicking outside
  useEffect(() => {
    if (!open) return;
    function handleClick(e) {
      if (panelRef.current && !panelRef.current.contains(e.target)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [open]);

  async function markRead(id) {
    try {
      await api.patch(`/treatment-alerts/${id}/read`);
    } catch (_) {}
    setAlerts((prev) => prev.filter((a) => a.id !== id));
    setUnread((prev) => Math.max(0, prev - 1));
  }

  async function markAllRead() {
    try {
      await api.patch("/treatment-alerts/read-all");
    } catch (_) {}
    setAlerts([]);
    setUnread(0);
  }

  async function goToTreatment(alert) {
    if (!alert.read) await markRead(alert.id);
    setOpen(false);
    if (alert.kind === "package") {
      navigate(`/pacientes/${alert.patient_id}?tab=procedures`);
    } else {
      navigate(`/pacientes/${alert.patient_id}?tab=treatments`);
    }
  }

  if (!isSecretary) return null;

  return (
    <div className="relative" ref={panelRef}>
      <button
        data-testid="treatment-alert-bell"
        onClick={() => setOpen((v) => !v)}
        className="relative flex h-10 w-10 items-center justify-center rounded-lg border border-[#DCD7CE] text-[#0B2230] transition-colors hover:bg-[#F4F1EA]"
        aria-label="Alertas de tratamento"
      >
        <Bell className="h-5 w-5" />
        {unread > 0 && (
          <span
            data-testid="treatment-alert-badge"
            className="absolute -right-1.5 -top-1.5 flex h-5 min-w-[20px] items-center justify-center rounded-full bg-[#DC2626] px-1 text-[10px] font-bold text-white shadow-sm"
          >
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div
          data-testid="treatment-alert-panel"
          className="absolute right-0 top-full mt-2 w-[400px] max-h-[480px] overflow-hidden rounded-xl border border-[#E6E1DC] bg-white shadow-[0_12px_40px_rgba(11,34,48,0.14)] z-50"
        >
          {/* Header */}
          <div className="flex items-center justify-between border-b border-[#E6E1DC] px-4 py-3">
            <h3 className="text-sm font-semibold text-[#0B2230]">Novos Tratamentos</h3>
            <div className="flex items-center gap-2">
              {unread > 0 && (
                <button
                  data-testid="treatment-alert-read-all"
                  onClick={markAllRead}
                  className="flex items-center gap-1 rounded-md px-2 py-1 text-[11px] font-medium text-[#059669] transition-colors hover:bg-[#ECFDF5]"
                >
                  <CheckCheck className="h-3.5 w-3.5" /> Marcar tudo como lido
                </button>
              )}
              <button onClick={() => setOpen(false)} className="rounded p-1 text-[#8C98A0] hover:bg-[#F4F1EA]">
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>

          {/* List */}
          <div className="max-h-[400px] overflow-y-auto">
            {alerts.length === 0 && (
              <div className="px-4 py-8 text-center text-sm text-[#8C98A0]">Nenhum alerta pendente</div>
            )}
            {alerts.map((a) => {
              const isPackage = a.kind === "package";
              const Icon = isPackage ? Brush : Syringe;
              const items = (a.items || []).map((i) => `${i.name} (${i.qty}x)`).join(", ");
              return (
                <div
                  key={a.id}
                  data-testid={`treatment-alert-item-${a.id}`}
                  className={`flex gap-3 border-b border-[#F4F1EA] px-4 py-3 transition-colors hover:bg-[#FAFAF8] cursor-pointer ${!a.read ? "bg-[#FFFBF5]" : ""}`}
                  onClick={() => goToTreatment(a)}
                >
                  <span
                    className={`mt-0.5 flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg ${
                      isPackage ? "bg-[#FDF4E8] text-[#8A5A2B]" : "bg-[#EFF6FF] text-[#1D4ED8]"
                    }`}
                  >
                    <Icon className="h-4 w-4" />
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-semibold text-[#0B2230] truncate">{a.patient_name}</span>
                      <div className="flex items-center gap-1.5 flex-shrink-0 ml-2">
                        {!a.read && <span className="h-2 w-2 rounded-full bg-[#DC2626]" />}
                        <span className="text-[10px] text-[#8C98A0]">{timeAgo(a.created_at)}</span>
                      </div>
                    </div>
                    <div className="mt-0.5 text-xs text-[#5C6B73] truncate">{items}</div>
                    <div className="mt-1 flex items-center justify-between">
                      <span className="font-mono text-xs font-bold text-[#0B2230]">{currency(a.total_price)}</span>
                      <span className="text-[10px] text-[#8C98A0]">
                        {a.created_by} ({ROLE_LABELS[a.created_by_role] || a.created_by_role})
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
